import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  InsuranceApiService,
  InsuranceCommissionAccrual,
  InsurancePolicyListItem,
  InsuranceProducerListItem
} from './insurance-api.service';

@Component({
  standalone: true,
  selector: 'ain-insurance-commission-accruals',
  imports: [
    AinAmountPipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    JalaliDateInputComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    SelectModule
  ],
  template: `
    <ain-page
      [title]="t('insurance.commissions.accruals.title')"
      [description]="t('insurance.commissions.accruals.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.commissions.accruals.help.page.title'"
        [descriptionKey]="'insurance.commissions.accruals.help.page.description'"
        [bulletKeys]="[
          'insurance.commissions.accruals.help.page.bullets.scope',
          'insurance.commissions.accruals.help.page.bullets.period',
          'insurance.commissions.accruals.help.page.bullets.status'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.commissions.accruals.help.listFilters.title'"
                [descriptionKey]="'insurance.commissions.accruals.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.commissions.accruals.help.listFilters.bullets.producer',
                  'insurance.commissions.accruals.help.listFilters.bullets.period',
                  'insurance.commissions.accruals.help.listFilters.bullets.status'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.commissions.accruals.filters.producer') }}</span>
                <p-select
                  [options]="producerOptions()"
                  optionLabel="label"
                  optionValue="value"
                  [(ngModel)]="producerId"
                  name="producerId"
                  [showClear]="true"
                  [filter]="true"
                  appendTo="body"
                  (ngModelChange)="load()" />
              </label>
              <label>
                <span>{{ t('insurance.commissions.accruals.filters.period') }}</span>
                <ain-date-input [(ngModel)]="periodDate" name="periodDate" (ngModelChange)="load()" />
              </label>
              <label>
                <span>{{ t('insurance.commissions.accruals.filters.status') }}</span>
                <p-select
                  [options]="accrualStatusOptions"
                  optionValue="value"
                  [(ngModel)]="statusFilter"
                  name="statusFilter"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.commissions.accruals.states.empty')" [message]="t('insurance.commissions.accruals.states.emptyMessage')" />
          } @else {
            <p-table [value]="filteredRows()" dataKey="id" [paginator]="true" [rows]="10">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('insurance.commissions.accruals.fields.period') }}</th>
                  <th>{{ t('insurance.commissions.accruals.fields.producer') }}</th>
                  <th>{{ t('insurance.commissions.accruals.fields.policy') }}</th>
                  <th>{{ t('insurance.commissions.accruals.fields.amount') }}</th>
                  <th>{{ t('insurance.commissions.accruals.fields.status') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.period }}</td>
                  <td>{{ producerLabel(row.producerId) }}</td>
                  <td>
                    <button type="button" class="link-btn" (click)="openPolicy(row.policyId)">{{ policyLabel(row.policyId) }}</button>
                  </td>
                  <td>{{ row.amount | ainAmount }}</td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .filter-grid { grid-template-columns: 1fr; } }
  `]
})
export class InsuranceCommissionAccrualsComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly error = signal('');
  readonly permissionDenied = signal(false);
  readonly rows = signal<InsuranceCommissionAccrual[]>([]);
  readonly producers = signal<InsuranceProducerListItem[]>([]);
  readonly policies = signal<InsurancePolicyListItem[]>([]);
  producerId: string | null = null;
  periodDate: string | null = null;
  statusFilter: string | null = null;

  readonly accrualStatusOptions = [
    { value: 'Accrued', labelKey: 'insurance.commissions.accruals.status.accrued' },
    { value: 'Paid', labelKey: 'insurance.commissions.accruals.status.paid' },
    { value: 'ClawedBack', labelKey: 'insurance.commissions.accruals.status.clawedBack' }
  ];

  ngOnInit(): void {
    this.api.listProducers().subscribe({
      next: rows => this.producers.set(rows),
      error: () => this.producers.set([])
    });
    this.api.listPolicies().subscribe({
      next: rows => this.policies.set(rows),
      error: () => this.policies.set([])
    });
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.listCommissionAccruals(this.producerId || null, this.periodQuery()).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.error.set('');
        this.permissionDenied.set(false);
        this.loading.set(false);
      },
      error: err => {
        this.rows.set([]);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
        this.loading.set(false);
      }
    });
  }

  filteredRows(): InsuranceCommissionAccrual[] {
    return this.rows().filter(row => !this.statusFilter || row.status === this.statusFilter);
  }

  producerOptions(): Array<{ label: string; value: string }> {
    return this.producers().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  producerLabel(producerId: string): string {
    const producer = this.producers().find(item => item.id === producerId);
    return producer ? `${producer.code} - ${producer.name}` : this.t('insurance.commissions.labels.unknownProducer');
  }

  policyLabel(policyId: string): string {
    const policy = this.policies().find(item => item.id === policyId);
    return policy?.policyNumber ?? this.t('insurance.commissions.labels.unknownPolicy');
  }

  openPolicy(policyId: string): void {
    void this.router.navigate(['/insurance/policies', policyId]);
  }

  statusKey(status: InsuranceCommissionAccrual['status']): string {
    return `insurance.commissions.accruals.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  private periodQuery(): string | null {
    if (!this.periodDate?.trim()) {
      return null;
    }

    return this.periodDate.trim().slice(0, 7);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
